import { MaxCouponDiscountPropertyRequiredParams } from '~/api/converters/domestic/order/Discount'
import { TDomesticCoupon } from '~/api/services/coupon/index.type'
import getMaxCouponDiscount from '~/api/converters/domestic/order/Coupons/MaxDiscountCoupons/getMaxCouponDiscount'
import { IDomesticOrderDiscountCouponSelectModalCoupon } from '~/api/converters/domestic/order/Discount/CouponSelectModal/index.type'
import { getCouponSelectModalPropertyItemCoupons } from '~/api/converters/domestic/order/Discount/CouponSelectModal/propertyCoupons'
import { TMaxDiscountLimit } from '~/api/routes/domestic/order/controller/type'

/** B2B 쿠폰 타입 상수 */
export const B2B_COUPON_TYPES = {
  /** 자동발급 쿠폰 */
  AUTO_ISSUE: 'AUTO_ISSUE',
  /** 쿠폰룸 쿠폰 */
  COUPON_ROOM: 'COUPON_ROOM',
  /** 후불형 쿠폰 */
  POSTPAY: 'POSTPAY',
  /** 수수료 할증 쿠폰 */
  OVERCHARGE: 'OVERCHARGE',
} as const
export type B2BCouponType = (typeof B2B_COUPON_TYPES)[keyof typeof B2B_COUPON_TYPES]

/** 모든 B2B 쿠폰 타입 배열 (OVERCHARGE 포함) */
export const NEW_B2B_COUPON_TYPES = [
  B2B_COUPON_TYPES.AUTO_ISSUE,
  B2B_COUPON_TYPES.COUPON_ROOM,
  B2B_COUPON_TYPES.POSTPAY,
  B2B_COUPON_TYPES.OVERCHARGE,
] as const

/** 피처 플래그에 따른 B2B 쿠폰 타입 배열을 반환합니다. */
export const getB2BCouponTypes = (): readonly B2BCouponType[] => {
  return NEW_B2B_COUPON_TYPES
}

/** 주어진 쿠폰 타입이 숙소 B2B 쿠폰인지 확인합니다. */
export const getIsPropertyB2BCoupon = (couponType: string): couponType is B2BCouponType => {
  const b2bCouponTypes = getB2BCouponTypes()
  return b2bCouponTypes.includes(couponType as B2BCouponType)
}

export const mapDomesticCouponDiscountAppliedItem = (
  coupon: TDomesticCoupon,
  typeIdx: number,
  property: MaxCouponDiscountPropertyRequiredParams
): IDomesticOrderDiscountCouponSelectModalCoupon => {
  return getCouponSelectModalPropertyItemCoupons({ coupons: [coupon], typeIdx, property })[0]
}

const getId = (c: TDomesticCoupon) => {
  return `${c.id ?? ''}:${c.metaCouponId}:${c.progress?.id ?? ''}`
}

/**
 *  @description  최대 할인 금액을 계산하기 위한 쿠폰 정보 변환
 *  @param  {TDomesticCoupon}  coupon  쿠폰 정보
 *
 *  @return  {Object}  쿠폰 정보 변환 결과
 *  @return  {string}  id  쿠폰 식별자
 *  @return  {number}  price  쿠폰 할인 금액
 *  @return  {number}  remainCount  쿠폰 잔여 재고. 고객 쿠폰인 경우 잔여 횟수를 1개라고 가정
 */
const mapDomesticCoupon = (coupon: TDomesticCoupon | null) => {
  if (!coupon) {
    return {
      id: '',
      price: 0,
      remainCount: 0,
    }
  }

  const remainCount = getIsPropertyB2BCoupon(coupon.couponType)
    ? (coupon.progress?.limitUse ?? 0) - (coupon.progress?.used ?? 0)
    : 1

  return {
    id: getId(coupon),
    price: coupon.calculatedPrice,
    remainCount,
  }
}

/**
 *  @description   숙소와 해당 숙소에서 적용 가능한 쿠폰 배열이 주어졌을 때 최대할인 금액을 계산하는 정책은 다음과 같다.
 *                            각 property 는 하나의 쿠폰만 할인 금액으로 적용 가능하다.
 *                            같은 쿠폰은 같은 id를 가진다.
 *                            쿠폰은 금액 순으로 정렬되어 있다.
 *                            상품 쿠폰은 재고가 있어야 적용 가능하다.
 *                            고객 쿠폰은 전 숙소에서 1번만 사용 가능하다.
 *                            이때 적용 가능한 쿠폰의 합이 최대가 되도록 구현
 *
 * @param  {Array}  properties  숙소 배열
 * @param  {number} properties.typeIdx  숙소 인덱스
 * @param  {number} properties.price  숙소 가격
 * @param  {Array}  properties.coupons  쿠폰 배열
 *
 * @return  {Object}  최대 할인 금액과 쿠폰이 적용된 숙소 정보
 * @return  {Array}  discountAppliedProperties  쿠폰이 적용된 숙소 정보
 * @return  {number}  maxDiscount 최대 할인 금액
 * */
export const getDomesticCouponMaxDiscount = (
  properties: MaxCouponDiscountPropertyRequiredParams[],
  maxDiscountLimit: TMaxDiscountLimit
) => {
  // 좀 더 효율적인 백트래킹을 위해 정렬
  const sortedProperties = [...properties].sort((a, b) => (b?.price ?? 0) - (a?.price ?? 0))

  const filterCoupon = (coupon: TDomesticCoupon | null) => {
    if (coupon === null) return true

    const remainCount = getIsPropertyB2BCoupon(coupon.couponType)
      ? (coupon.progress?.limitUse ?? 0) - (coupon.progress?.used ?? 0)
      : 1
    return coupon.unavailableReasons.length === 0 && remainCount > 0
  }

  return getMaxCouponDiscount<TDomesticCoupon>({
    maxDiscountLimit,
    items: sortedProperties,
    filterCoupon,
    mapCoupon: mapDomesticCoupon,
    mapCouponDiscountAppliedItem: (coupon, typeIdx) =>
      mapDomesticCouponDiscountAppliedItem(coupon, typeIdx, properties[typeIdx]),
  })
}
